import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Mic, Circle, Video } from 'lucide-react';
import { createClient, LiveTranscriptionEvents } from '@deepgram/sdk';
import { toast } from 'react-hot-toast';
import { useAuth } from '@clerk/clerk-react';
import { useAuthenticatedFetch } from '../utils/api';
import { useTheme } from '../contexts/ThemeContext';

const MeetingRecorder = () => {
  const navigate = useNavigate();
  const { isLoaded, isSignedIn } = useAuth();
  const authenticatedFetch = useAuthenticatedFetch();
  const { theme } = useTheme();
  
  const [isRecording, setIsRecording] = useState(false);
  const [transcript, setTranscript] = useState('');
  const [title, setTitle] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const mediaRecorderRef = useRef(null);
  const socketRef = useRef(null);
  const deepgramRef = useRef(null);
  const [audioUrl, setAudioUrl] = useState(null);
  const [audioFile, setAudioFile] = useState(null);
  const [isUploading, setIsUploading] = useState(false);

  // Initialize Deepgram client when component mounts
  useEffect(() => {
    deepgramRef.current = createClient(process.env.REACT_APP_DEEPGRAM_API_KEY);
    
    return () => {
      if (socketRef.current) {
        socketRef.current.finish();
      }
      if (mediaRecorderRef.current) {
        mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  const handleProcessTranscript = async (transcript, meetingId) => {
    try {
      const response = await authenticatedFetch(
        'https://classify-alpha-a1e5331e0be0.herokuapp.com/api/process-meeting-transcript',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            transcript,
            meetingId
          })
        }
      );

      if (!response.ok) {
        throw new Error('Failed to process transcript');
      }

      const data = await response.json();
      return data;
    } catch (error) {
      console.error('Error processing transcript:', error);
      toast.error('Failed to process transcript');
      throw error;
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (!file) return;

    if (!title.trim()) {
      toast.error('Please enter a meeting title before uploading');
      return;
    }

    try {
      setIsUploading(true);
      toast.loading('Uploading audio file...');

      // Create FormData for file upload
      const formData = new FormData();
      formData.append('audio', file);

      // Upload audio file
      const uploadResponse = await authenticatedFetch(
        'https://classify-alpha-a1e5331e0be0.herokuapp.com/api/upload-audio',
        {
          method: 'POST',
          headers: {},
          body: formData
        }
      );

      if (!uploadResponse.ok) {
        throw new Error('Failed to upload audio file');
      }

      const { audioUrl } = await uploadResponse.json();

      // Transcribe audio
      const transcribeResponse = await authenticatedFetch(
        'https://classify-alpha-a1e5331e0be0.herokuapp.com/api/transcribe',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({ audioUrl })
        }
      );

      if (!transcribeResponse.ok) {
        throw new Error('Failed to transcribe audio');
      }

      const { transcript } = await transcribeResponse.json();

      // Save meeting with raw transcript
      const saveMeetingResponse = await authenticatedFetch(
        'https://classify-alpha-a1e5331e0be0.herokuapp.com/api/save-meeting',
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json'
          },
          body: JSON.stringify({
            title: title || 'Untitled Meeting',
            date: new Date().toISOString(),
            transcript,
            audioUrl
          })
        }
      );

      if (!saveMeetingResponse.ok) {
        throw new Error('Failed to save meeting');
      }

      const meeting = await saveMeetingResponse.json();

      // Process the transcript
      await handleProcessTranscript(transcript, meeting.id);

      toast.dismiss();
      toast.success('Meeting uploaded and processed successfully');
      navigate(`/meetings/${meeting.id}`);
    } catch (error) {
      console.error('Error handling file upload:', error);
      toast.dismiss();
      toast.error('Failed to process meeting recording');
    } finally {
      setIsUploading(false);
    }
  };

  const startRecording = async () => {
    if (!title.trim()) {
      toast.error('Please enter a meeting title before recording');
      return;
    }

    try {
      if (!deepgramRef.current) {
        throw new Error('Deepgram client not initialized');
      }

      setTranscript('');
      
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      mediaRecorderRef.current = new MediaRecorder(stream);
      const audioChunks = [];

      mediaRecorderRef.current.addEventListener('dataavailable', (event) => {
        if (event.data.size > 0) {
          audioChunks.push(event.data);
          if (socketRef.current) {
            socketRef.current.send(event.data);
          }
        }
      });

      mediaRecorderRef.current.addEventListener('stop', async () => {
        const audioBlob = new Blob(audioChunks, { type: 'audio/webm' });
        const file = new File([audioBlob], `meeting-${Date.now()}.webm`, { type: 'audio/webm' });
        setAudioFile(file);
        try {
          // Create FormData for file upload
          const formData = new FormData();
          formData.append('audio', file);

          // Upload audio file
          const uploadResponse = await authenticatedFetch(
            'https://classify-alpha-a1e5331e0be0.herokuapp.com/api/upload-audio',
            {
              method: 'POST',
              body: formData
            }
          );

          if (!uploadResponse.ok) {
            throw new Error('Failed to upload audio file');
          }

          const { audioUrl } = await uploadResponse.json();
          setAudioUrl(audioUrl);

          // Get the final transcript
          const finalTranscript = transcript;

          // Save meeting with raw transcript
          const saveMeetingResponse = await authenticatedFetch(
            'https://classify-alpha-a1e5331e0be0.herokuapp.com/api/save-meeting',
            {
              method: 'POST',
              headers: {
                'Content-Type': 'application/json'
              },
              body: JSON.stringify({
                title: title || 'Untitled Meeting',
                date: new Date().toISOString(),
                transcript: finalTranscript,
                audioUrl
              })
            }
          );

          if (!saveMeetingResponse.ok) {
            throw new Error('Failed to save meeting');
          }

          const meeting = await saveMeetingResponse.json();

          // Process the transcript
          console.log('Processing transcript:', finalTranscript);
          const processedResult = await handleProcessTranscript(finalTranscript, meeting.id);
          console.log('Processed result:', processedResult);

          toast.success('Meeting recorded and processed successfully');
          navigate(`/meetings/${meeting.id}`);
        } catch (error) {
          console.error('Error uploading audio:', error);
          toast.error('Failed to process meeting recording');
        }
      });

      const connection = deepgramRef.current.listen.live({
        language: 'en-US',
        smart_format: true,
        model: 'nova-3',
        interim_results: false,
      });

      connection.addListener(LiveTranscriptionEvents.Open, () => {
        mediaRecorderRef.current.start(1000);
        setIsRecording(true);
      });

      connection.addListener(LiveTranscriptionEvents.Close, () => {
        console.log('Deepgram connection closed');
      });

      connection.addListener(LiveTranscriptionEvents.Transcript, (data) => {
        if (data?.channel?.alternatives?.[0]?.transcript) {
          const receivedTranscript = data.channel.alternatives[0].transcript;
          setTranscript(prevTranscript => {
            if (!prevTranscript.includes(receivedTranscript)) {
              return (prevTranscript + ' ' + receivedTranscript).trim();
            }
            return prevTranscript;
          });
        }
      });

      connection.addListener(LiveTranscriptionEvents.Error, (error) => {
        console.error('Deepgram error:', error);
        toast.error('Transcription error occurred');
      });

      socketRef.current = connection;

    } catch (error) {
      console.error('Error starting recording:', error);
      toast.error('Failed to start recording');
    }
  };

  const stopRecording = async () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      if (socketRef.current) {
        socketRef.current.finish();
      }
      setIsRecording(false);
      
      mediaRecorderRef.current.stream.getTracks().forEach(track => track.stop());
      
      // Save the meeting
      try {
        setIsProcessing(true);
        const meetingData = {
          title: title.trim(),
          date: new Date().toISOString(),
          transcript: transcript,
          audioUrl: audioUrl
        };

        const response = await authenticatedFetch(
          'https://classify-alpha-a1e5331e0be0.herokuapp.com/api/save-meeting',
          {
            method: 'POST',
            body: JSON.stringify(meetingData)
          }
        );

        if (!response.ok) {
          throw new Error('Failed to save meeting');
        }

        const savedMeeting = await response.json();
        toast.success('Meeting saved successfully');
        navigate(`/meetings/${savedMeeting.id}`);
      } catch (error) {
        console.error('Error saving meeting:', error);
        toast.error('Failed to save meeting');
      } finally {
        setIsProcessing(false);
      }
    }
  };

  if (!isLoaded) {
    return <div>Loading...</div>;
  }

  return (
    <div className="min-h-screen bg-theme theme-transition">
      {/* Header */}
      <header className="bg-theme border-b border-theme theme-transition">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <nav className="flex items-center text-sm mb-4 overflow-x-auto whitespace-nowrap pb-1">
            <button
              onClick={() => navigate('/meetings')}
              className="text-accent hover:text-theme hover:underline"
            >
              Meetings
            </button>
            <span className="mx-2 text-accent">/</span>
            <span className="text-theme">
              New Meeting
            </span>
          </nav>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-6">
            <div className="flex-1 max-w-2xl">
              <input
                type="text"
                placeholder="Enter meeting title..."
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                className="w-full text-xl sm:text-2xl font-bold px-0 border-0 focus:ring-0 bg-theme text-theme placeholder:text-accent"
              />
              <p className="text-sm text-accent">
                {new Date().toLocaleDateString('en-US', { 
                  weekday: 'long',
                  month: 'long',
                  day: 'numeric'
                })}
              </p>
            </div>
            
            <div className="flex items-center gap-3 w-full sm:w-auto">
              {isRecording ? (
                <button
                  onClick={stopRecording}
                  disabled={isProcessing}
                  className="w-full sm:w-auto px-4 py-2 rounded-lg flex items-center justify-center gap-2 bg-red-50 text-red-600 hover:bg-red-100 transition-all"
                >
                  <Circle size={20} className="animate-pulse" />
                  {isProcessing ? 'Processing...' : 'Stop Recording'}
                </button>
              ) : (
                <>
                  <button
                    onClick={startRecording}
                    className="w-full sm:w-auto px-4 py-2 rounded-lg flex items-center justify-center gap-2 bg-red-500 text-white hover:bg-red-600 transition-all"
                  >
                    <Mic size={20} />
                    Start Recording
                  </button>
                  <label className="w-full sm:w-auto px-4 py-2 rounded-lg flex items-center justify-center gap-2 bg-secondary text-accent hover:text-theme transition-all cursor-pointer">
                    <Video size={20} />
                    <span className="whitespace-nowrap">Upload Audio</span>
                    <input 
                      type="file" 
                      accept="audio/*" 
                      className="hidden" 
                      onChange={handleFileUpload}
                      disabled={isUploading}
                    />
                  </label>
                </>
              )}
            </div>
          </div>
        </div>
      </header>

      {/* Content Area */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex flex-col max-h-[calc(100vh-200px)]">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-lg font-medium text-theme">Live Transcript</h2>
          </div>

          <div className="flex-1 bg-secondary rounded-lg p-4 overflow-y-auto">
            {transcript ? (
              <div className="whitespace-pre-wrap text-accent">
                {transcript}
              </div>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-accent">
                <Video size={48} className="mb-4" />
                <p className="text-center">
                  {isRecording 
                    ? 'Listening...' 
                    : 'Start recording to see the live transcript'}
                </p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default MeetingRecorder; 